import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    TextField,
    IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useToast } from '../providers/toaster-provider';
import { useGeolocation } from '../providers/location-provider';
import Dropdown from './ui/dropdown';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

interface Company {
    entityId: string | number;
    entityName: string;
    role?: string;
}

interface CompanySetupProps {
    onSuccess?: (company: Company) => void;
    onBack?: () => void;
}

const CompanySetup: React.FC<CompanySetupProps> = ({ onSuccess, onBack }) => {
    const navigate = useNavigate();
    const { showError, showSuccess } = useToast();
    const { country: geoLocation } = useGeolocation();
    const [form, setForm] = useState({ company: '', country: '' });
    const [errors, setErrors] = useState<{ company?: string; country?: string }>({});
    const [countries, setCountries] = useState<{ value: string, label: string }[]>([]);
    const [createCompany, setCreateCompany] = useApi();
    const [fetchCountries, setFetchCountries] = useApi();

    // Fetch countries on mount
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const { url, method } = buildApiRequestConfig(routes.country.fetch);
                setFetchCountries({ url, method });
            } catch (error) {
                console.error('Failed to fetch countries:', error);
            }
        };
        fetchCountries();
    }, []);

    // Handle fetched countries and auto-select based on geolocation
    useEffect(() => {
        const { data, isLoading, error } = fetchCountries;
        if (data && !isLoading) {
            const countryNames = data.map((c: any) => c.country);
            const countryOptions = data.map((item: any) => ({
                value: String(item.id),
                label: item.country,
            }));
            setCountries(countryOptions);

            // Auto-select country if geolocation is supported and not already selected
            if (geoLocation && countryNames.includes(geoLocation)) {
                setForm(prev => {
                    if (!prev.country) {
                        const countryId = data.find((c: any) => c.country === geoLocation)?.id;
                        return { ...prev, country: String(countryId) };
                    }
                    return prev;
                });
            }
        }
    }, [fetchCountries, geoLocation]);

    // Handle create company API response
    useEffect(() => {
        const { data, isLoading, error } = createCompany;
        if (data && !isLoading) {
            showSuccess('Company created successfully!');
                const newCompany: Company = {
                    entityId: data.entityId,
                    entityName: data.entityName,
                    role: 'admin'
                };
                
                const existingEntities = Cookies.get('entities');
                const entities = existingEntities ? JSON.parse(existingEntities) : [];
                entities.push(newCompany);
                Cookies.set('entities', JSON.stringify(entities));
                onBack && onBack();
        } else if (createCompany.error) {
            setErrors({ company: 'Failed to create company' });
        }
    }, [createCompany, showSuccess]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleCountryChange = (value: string) => {
        setForm(prev => ({ ...prev, country: value }));
        setErrors(prev => ({ ...prev, country: '' }));
    };

    const validate = () => {
        const newErrors: { company?: string; country?: string } = {};
        if (!form.company.trim()) {
            newErrors.company = 'Company name is required';
        }
        if (!form.country) {
            newErrors.country = 'Country is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        const { url, method } = buildApiRequestConfig(routes.company.create);
        setCreateCompany({
            url,
            method,
            body: {
                entityName: form.company,
                countryId: form.country
            }
        });
    };

    return (
        <Box>
            {/* Header with back button */}
            <Box display="flex" alignItems="center" mb={3}>
                {onBack && (
                    <IconButton 
                        onClick={onBack}
                        size="small" 
                        sx={{ mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Typography fontSize={16} fontWeight={600}>
                    Create New Company
                </Typography>
            </Box>

            <Typography color="text.secondary" mb={3}>
                Please provide the details below to set up your new company.
            </Typography>

            {/* Company Setup Form */}
            <Box my={2}>
                <Box display="flex" justifyContent="space-between" gap={1} flexDirection="column">
                    <TextField
                        label="Company Name"
                        required
                        name="company"
                        fullWidth
                        value={form.company}
                        onChange={handleChange}
                        error={!!errors.company}
                        helperText={errors.company}
                        placeholder="Enter your company name"
                    />
                    <Dropdown
                        value={form.country}
                        options={countries}
                        height={55}
                        title="Country"
                        placeholder="Select Country"
                        error={!!errors.country}
                        helperText={errors.country}
                        onChange={(value: string) => {
                            handleCountryChange(value);
                        }}
                    />
                </Box>
            </Box>

            {/* Action Buttons */}
            <Box display="flex" gap={2} justifyContent="space-between" mt={4}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onBack}
                    disabled={createCompany.isLoading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: 'var(--color-primary)',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: 'var(--color-primary)',
                        },
                        '&:disabled': {
                            backgroundColor: '#ccc',
                            color: 'var(--color-text-muted)',
                        },
                    }}
                    onClick={handleSubmit}
                    disabled={createCompany.isLoading || !form.company || !form.country}
                    fullWidth={false}
                >
                    {createCompany.isLoading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                    {createCompany.isLoading ? 'Creating...' : 'Create'}
                </Button>
            </Box>
        </Box>
    );
};

export default CompanySetup;
